import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { Suspense, lazy, ComponentType } from 'react';
import LazySpinner from './components/common/LazySpinner';
import ConsentGuard from './components/Consent/ConsentGuard';
// 미동의 필수공지 관문 — 아래 path:'/' 그룹 element 한 곳에서 하위 전 라우트를 커버한다.
//  면제 경로(마켓홈·로그인·/app·상점약관)는 관문 내부 isExemptPath 가 pathname 으로 거른다.
//  ★lazy 가 아닌 정적 import: 관문은 자식보다 먼저 판정해야 하고, 자체 chunk 를 기다리면
//    페이지 진입마다 스피너가 한 번 더 늘어난다. 파일 자체도 래퍼 한 줄이라 번들 영향 미미.
import RequiredNoticeGuard from './components/common/RequiredNoticeGuard';
// 미동의 약관 관문 — 공지 관문 바깥에서 같은 그룹을 감싼다(면제 경로 목록도 동일).
import TermsGuard from './components/common/TermsGuard';
// [APP-FORK] 앱이 아니면(웹 브라우저·카카오 인앱 등) claim 웹 렌더 차단 + 앱 유도. claim 라우트에만 적용.
import AppOnlyGuard from './app/AppOnlyGuard';
// [2026-10-31] 오류 화면은 정적 import — chunk 로드 실패를 보여주는 화면까지 chunk 로 받으면 함께 실패해 백지가 된다.
import ErrorPage from './error-page';

// Lazy load components with type annotations
const CommonRouter = lazy(
  () => import('./Main') as Promise<{ default: ComponentType }>
);
const UserHome = lazy(
  () => import('./components/Home/Home') as Promise<{ default: ComponentType }>
);
const StoreInfo = lazy(
  () =>
    import('./components/StoreInfo/StoreInfo') as Promise<{
      default: ComponentType;
    }>
);
const UserLogin = lazy(
  () =>
    import('./components/Login/Login') as Promise<{ default: ComponentType }>
);
const Gift = lazy(
  () => import('./components/Gift/Gift') as Promise<{ default: ComponentType }>
);
const SelectGift = lazy(
  () =>
    import('./components/SelectGift/SelectGift') as Promise<{
      default: ComponentType;
    }>
);
const ReceivedRoot = lazy(
  () =>
    import('./components/Recevied/ReceivedRoot') as Promise<{
      default: ComponentType;
    }>
);
const Claim = lazy(
  () =>
    import('./components/Claim/Claim') as Promise<{ default: ComponentType }>
);
const Checkout = lazy(
  () =>
    import('./components/Checkout/Checkout') as Promise<{
      default: ComponentType;
    }>
);
const QRCode = lazy(
  () => import('./components/QRCode') as Promise<{ default: ComponentType }>
);
const KaKaoCallback = lazy(
  () =>
    import('./components/Login/KaKaoCallback') as Promise<{
      default: ComponentType;
    }>
);
const NaverCallback = lazy(
  () =>
    import('./components/Login/NaverCallback') as Promise<{
      default: ComponentType;
    }>
);
const PaymentSuccessAfter = lazy(
  () =>
    import('./components/TossPayment/PaymentAfter') as Promise<{
      default: ComponentType;
    }>
);
const PaymentConfirm = lazy(
  () =>
    import('./components/TossPayment/CardApprovalSuccess') as Promise<{
      default: ComponentType;
    }>
);
const PaymentFail = lazy(
  () =>
    import('./components/TossPayment/CardApprovalFailure') as Promise<{
      default: ComponentType;
    }>
);

const SentRoot = lazy(
  () =>
    import('./components/Sent/SentRoot') as Promise<{ default: ComponentType }>
);
const ReceivedGiftDetail = lazy(
  () =>
    import('./components/Recevied/ReceivedGiftDetail') as Promise<{
      default: ComponentType;
    }>
);
const VerificationSuccess = lazy(
  () =>
    import('./components/Recevied/VerificationSuccess') as Promise<{
      default: ComponentType;
    }>
);
const VerificationFailure = lazy(
  () =>
    import('./components/Recevied/VerificationFailure') as Promise<{
      default: ComponentType;
    }>
);
const SentGiftDetail = lazy(
  () =>
    import('./components/Sent/SentGiftDetail') as Promise<{
      default: ComponentType;
    }>
);

const GoogleCallback = lazy(
  () =>
    import('./components/Login/GoogleCallback') as Promise<{
      default: ComponentType;
    }>
);
const Refund = lazy(
  () =>
    import('./components/Refund/Refund') as Promise<{ default: ComponentType }>
);
const StoreConsent = lazy(
  () =>
    import('./components/Consent/StoreConsent') as Promise<{
      default: ComponentType;
    }>
);
const PrivacyPolicy = lazy(
  () =>
    import('./components/Consent/PrivacyPolicy') as Promise<{
      default: ComponentType;
    }>
);
const ServiceTerms = lazy(
  () =>
    import('./components/Consent/ServiceTerms') as Promise<{
      default: ComponentType;
    }>
);
const NaegiftAppTerms = lazy(
  () =>
    import('./components/Consent/NaegiftAppTerms') as Promise<{
      default: ComponentType;
    }>
);
const NaegiftAppPrivacy = lazy(
  () =>
    import('./components/Consent/NaegiftAppPrivacy') as Promise<{
      default: ComponentType;
    }>
);
const AllGiftsHome = lazy(
  () =>
    import('./components/Stores/AllGiftsHome') as Promise<{
      default: ComponentType;
    }>
);
const AllReceivedHome = lazy(
  () =>
    import('./components/Stores/AllReceivedHome') as Promise<{
      default: ComponentType;
    }>
);
const AllSentHome = lazy(
  () =>
    import('./components/Stores/AllSentHome') as Promise<{
      default: ComponentType;
    }>
);
const CouponBox = lazy(
  () =>
    import('./components/Coupon/CouponBox') as Promise<{
      default: ComponentType;
    }>
);
// [2026-10-22 A-F1] 쿠폰받기
const CouponClaim = lazy(
  () =>
    import('./components/Coupon/CouponClaim') as Promise<{
      default: ComponentType;
    }>
);
// [2026-10-22 B-F1] 앱 이벤트 룰렛(로그인 회원 1인 1회) / 현장 공개 URL 폐지 안내
const EventRoulette = lazy(
  () =>
    import('./components/Promotion/Roulette') as Promise<{
      default: ComponentType;
    }>
);
const RouletteMoved = lazy(
  () =>
    import('./components/Promotion/RouletteMoved') as Promise<{
      default: ComponentType;
    }>
);
const GlobalLogin = lazy(
  () =>
    import('./components/Login/GlobalLogin') as Promise<{
      default: ComponentType;
    }>
);
const TestDashboard = lazy(
  () =>
    import('./components/TestDashboard/TestDashboard') as Promise<{
      default: ComponentType;
    }>
);
const MyInfo = lazy(
  () =>
    import('./components/MyInfo/MyInfo') as Promise<{ default: ComponentType }>
);
// [2026-10-27] 룰렛 경품 구매 계정 연결 팝업 (운영자 · 채널 · 상점관리 화면이 연다)
const PurchaseAccountLink = lazy(
  () =>
    import('./components/PurchaseAccount/PurchaseAccountLink') as Promise<{
      default: ComponentType;
    }>
);
const NoticeBoard = lazy(
  () =>
    import('./components/Notice/NoticeBoard') as Promise<{
      default: ComponentType;
    }>
);
const NoticeDetail = lazy(
  () =>
    import('./components/Notice/NoticeDetail') as Promise<{
      default: ComponentType;
    }>
);
const Survey = lazy(
  () =>
    import('./components/Survey/Survey') as Promise<{
      default: ComponentType;
    }>
);
const UserGuide = lazy(
  () =>
    import('./components/Guide/UserGuide') as Promise<{
      default: ComponentType;
    }>
);
// [APP-FORK] 앱 통합 QR 진입 페이지 (naegift://{type}/{uuid} → /app/qr/:type/:uuid)
const AppQrEntry = lazy(
  () =>
    import('./components/app/AppQrEntry') as Promise<{
      default: ComponentType;
    }>
);
// [APP-FORK] QR 웹URL 랜딩(외부 카메라 폴백) — /app/link/:type/:uuid → 딥링크/스토어 유도
const AppLinkLanding = lazy(
  () =>
    import('./app/AppLinkLanding') as Promise<{
      default: ComponentType;
    }>
);

const router = createBrowserRouter(
  [
    {
      element: (
        <Suspense fallback={<LazySpinner />}>
          <CommonRouter />
        </Suspense>
      ),
      errorElement: <ErrorPage />,
      children: [
        {
          path: '/',
          // 관문은 이 그룹 element 한 곳에만 둔다 — 하위 전 라우트를 커버한다.
          //  ★element 가 없던 pathless 그룹은 자동으로 <Outlet /> 이었으므로, 명시적 Outlet 으로
          //    바꿔도 자식 렌더 동작은 동일하다(관문 래핑만 추가된다).
          //  ★여기는 CommonRouter 의 <Outlet />(Main.tsx:79) 하위 = LanguageProvider(Main.tsx:68)
          //    안쪽이라 NoticePopup 의 useLanguage 가 안전하다. CommonRouter 를 감싸는 자리(:205-215)
          //    는 Provider 바깥이라 throw 한다.
          //  ★약관이 공지보다 바깥 — 약관 미동의 상태에서 공지 동의를 먼저 받지 않는다.
          element: (
            <TermsGuard>
              <RequiredNoticeGuard>
                <Outlet />
              </RequiredNoticeGuard>
            </TermsGuard>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <AllGiftsHome />
                </Suspense>
              ),
            },
            {
              // [APP-FORK] 앱 통합 QR 진입 (/app/ 접두사 → /:storeNo 와 충돌 없음)
              path: '/app/qr/:type/:uuid',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <AppQrEntry />
                </Suspense>
              ),
            },
            {
              // [APP-FORK] QR 웹URL 랜딩(외부 카메라 폴백). 앱 스캐너는 /app/qr로 직행하므로 미경유
              path: '/app/link/:type/:uuid',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <AppLinkLanding />
                </Suspense>
              ),
            },
            {
              path: '/:storeNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  {/* [APP-FORK] 레거시 상점 QR(/:storeNo) 웹 접근 차단 → 앱 유도.
                      앱 웹뷰(isApp()=true)만 통과하므로 /app/qr 역매핑 redirect(앱 내부)는
                      정상 통과하고, 외부 웹 브라우저 스캔만 막힌다. claim(:533)과 동일 방식. */}
                  <AppOnlyGuard>
                    <UserHome />
                  </AppOnlyGuard>
                </Suspense>
              ),
            },
            {
              path: '/received',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <AllReceivedHome />
                </Suspense>
              ),
            },
            {
              path: '/sent',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <AllSentHome />
                </Suspense>
              ),
            },
            {
              // 쿠폰함(docs/promotion-ledger.html §15) — /received, /sent 와 동일한 글로벌 페이지.
              path: '/coupons',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <CouponBox />
                </Suspense>
              ),
            },
            {
              // [2026-10-22 A-F1] 쿠폰받기 — 통합선물함 쿠폰함(받은 쿠폰 보기)과 별개 페이지.
              //  공지사항 쿠폰이벤트의 [쿠폰 받으러 가기]로 ?promotionNo= 와 함께 들어온다.
              path: '/coupon-claim',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <CouponClaim />
                </Suspense>
              ),
            },
            {
              // [2026-10-22 B-F1] 앱 이벤트 룰렛 — 공지 [룰렛 하러 가기]로 진입, 로그인 회원만
              path: '/event/roulette/:promotionNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <EventRoulette />
                </Suspense>
              ),
            },
            {
              path: '/received/detail/:userGiftNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ReceivedGiftDetail />
                </Suspense>
              ),
            },
            {
              path: '/sent/detail/:purchaseNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <SentGiftDetail />
                </Suspense>
              ),
            },
            {
              path: '/gifts',
              element: <Navigate to="/" replace />,
            },
            {
              path: '/gifts/received',
              element: <Navigate to="/received" replace />,
            },
            {
              path: '/gifts/sent',
              element: <Navigate to="/sent" replace />,
            },
            {
              path: '/purchase-account/link',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <PurchaseAccountLink />
                </Suspense>
              ),
            },
            {
              path: '/login',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <GlobalLogin />
                </Suspense>
              ),
            },
            {
              path: '/notice',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <NoticeBoard />
                </Suspense>
              ),
            },
            {
              // [notice-002] 공지 상세. 겹치는 2세그먼트 변수 라우트 없음(/:storeNo/<고정어>, /login/:storeNo 뿐)
              path: '/notice/:id',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <NoticeDetail />
                </Suspense>
              ),
            },
            {
              path: '/survey',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <Survey />
                </Suspense>
              ),
            },
            {
              path: '/myinfo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <MyInfo />
                </Suspense>
              ),
            },
            {
              // 이용 안내(글로벌) — 마켓 드로어(storeNo 없는 진입)용.
              //  UserGuide 는 storeNo 부재를 이미 분기 처리한다(UserGuide.tsx:21 navigate('/'),
              //  :133 라벨 '마켓으로') → 컴포넌트 수정 없이 같은 것을 재사용한다.
              //  ★'/:storeNo/guide'(아래)와 공존한다 — 세그먼트 수가 달라 경쟁하지 않는다.
              path: '/guide',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <UserGuide />
                </Suspense>
              ),
            },
            {
              path: '/:storeNo/myinfo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <MyInfo />
                </Suspense>
              ),
            },
            {
              path: '/:storeNo/guide',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <UserGuide />
                </Suspense>
              ),
            },
            {
              // [APP-FORK] 앱 전용 상점정보 — 지류쿠폰 뒷면 QR(store-info 스킴)이 이 라우트로 온다.
              //   웹은 NavBar에 탭이 없어 자연 진입하지 않지만, QR 직접 스캔(모바일 웹)은
              //   AppOnlyGuard로 차단 → 앱 유도(PC 웹은 기존 정책대로 통과).
              path: '/:storeNo/store-info',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <AppOnlyGuard>
                    <StoreInfo />
                  </AppOnlyGuard>
                </Suspense>
              ),
            },
            {
              // [2026-10-27] 상점 화면 공지 목록 — 그 상점·참여 채널 공지 + 그 상점이 들어간 운영자 이벤트 공지
              path: '/:storeNo/notice',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <AppOnlyGuard>
                    <NoticeBoard />
                  </AppOnlyGuard>
                </Suspense>
              ),
            },
            {
              path: '/test',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <TestDashboard />
                </Suspense>
              ),
            },
            {
              path: '/login/oauth2/code/kakao',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <KaKaoCallback />
                </Suspense>
              ),
            },
            {
              path: '/login/oauth2/code/naver',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <NaverCallback />
                </Suspense>
              ),
            },
            {
              path: '/auth/google/callback',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <GoogleCallback />
                </Suspense>
              ),
            },
            {
              path: '/login/:storeNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <UserLogin />
                </Suspense>
              ),
            },
            {
              path: '/:storeNo/consent',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <StoreConsent />
                </Suspense>
              ),
            },
            {
              path: '/privacy',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <PrivacyPolicy />
                </Suspense>
              ),
            },
            {
              path: '/service-terms',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ServiceTerms />
                </Suspense>
              ),
            },
            {
              // 마켓(내기프트) 약관 전문 — TermsPopup 의 [보기] 대상.
              //  ★TermsGuard/RequiredNoticeGuard 면제 대상이다(두 가드의 isExemptPath).
              path: '/naegift-app-terms',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <NaegiftAppTerms />
                </Suspense>
              ),
            },
            {
              // 마켓(내기프트) 개인정보처리방침 전문 — 위와 동일.
              path: '/naegift-app-privacy',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <NaegiftAppPrivacy />
                </Suspense>
              ),
            },
            {
              path: '/gift/:storeNo/:giftNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  {/* [APP-FORK] 레거시 기프트 QR(/gift/:storeNo/:giftNo) 웹 접근 차단 → 앱 유도.
                      앱 웹뷰(isApp()=true)만 통과하므로 /app/qr/gift 역매핑 redirect(앱 내부)는
                      정상 통과하고, 외부 웹 브라우저만 막힌다. 상점(:249)·claim(:533)과 동일 방식.
                      기존 ConsentGuard 는 그대로 안쪽에 유지. */}
                  <AppOnlyGuard>
                    <ConsentGuard>
                      <Gift />
                    </ConsentGuard>
                  </AppOnlyGuard>
                </Suspense>
              ),
            },
            {
              path: '/selectGift/:storeNo/:giftNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <SelectGift />
                  </ConsentGuard>
                </Suspense>
              ),
            },
            {
              path: '/PaymentConfirm/:storeNo/:giftNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <PaymentConfirm />
                  </ConsentGuard>
                </Suspense>
              ),
            },
            {
              path: '/PaymentSuccessAfter/:storeNo/:purshaseNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <PaymentSuccessAfter />
                  </ConsentGuard>
                </Suspense>
              ),
            },
            {
              path: '/paymentfail/:storeNo/:purshaseNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <PaymentFail />
                  </ConsentGuard>
                </Suspense>
              ),
            },

            {
              path: '/:storeNo/received/:status',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <ReceivedRoot />
                  </ConsentGuard>
                </Suspense>
              ),
            },
            {
              path: '/:storeNo/verify',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <ReceivedRoot />
                  </ConsentGuard>
                </Suspense>
              ),
            },
            {
              path: '/:storeNo/verify/success',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <VerificationSuccess />
                  </ConsentGuard>
                </Suspense>
              ),
            },
            {
              path: '/:storeNo/verify/failure',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <VerificationFailure />
                  </ConsentGuard>
                </Suspense>
              ),
            },
            {
              path: '/:storeNo/received/detail/:userGiftNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <ReceivedGiftDetail />
                  </ConsentGuard>
                </Suspense>
              ),
            },
            {
              path: '/:storeNo/received',
              element: <Navigate to="unused" replace />,
            },
            {
              path: '/:storeNo/sent/:status',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <SentRoot />
                  </ConsentGuard>
                </Suspense>
              ),
            },
            {
              path: '/:storeNo/sent/detail/:purchaseNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <SentGiftDetail />
                  </ConsentGuard>
                </Suspense>
              ),
            },
            {
              path: '/:storeNo/sent',
              element: <Navigate to="unused" replace />,
            },
            {
              // [2026-10-22 C-F1] 상점 쿠폰함 — 이 상점에서 쓸 수 있는 내 쿠폰만
              path: '/:storeNo/coupons',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <CouponBox />
                  </ConsentGuard>
                </Suspense>
              ),
            },
            {
              path: 'claim/:storeNo/:purchaseNo/:uuid',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  {/* [APP-FORK] 앱이 아니면 차단 화면 → Claim 마운트 자체를 막는다. */}
                  <AppOnlyGuard>
                    <ConsentGuard>
                      <Claim />
                    </ConsentGuard>
                  </AppOnlyGuard>
                </Suspense>
              ),
            },
            {
              path: 'checkout/:storeNo/:giftNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <Checkout />
                  </ConsentGuard>
                </Suspense>
              ),
            },
            {
              path: 'qrcode',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <QRCode />
                </Suspense>
              ),
            },
            {
              path: 'refund/:storeNo/:purchaseNo',
              element: (
                <Suspense fallback={<LazySpinner />}>
                  <ConsentGuard>
                    <Refund />
                  </ConsentGuard>
                </Suspense>
              ),
            },
          ],
        },
        {
          path: '/giftshop-kor-demo',
          element: (
            <Suspense fallback={<LazySpinner />}>
              <UserHome />
            </Suspense>
          ),
        },
      ],
    },
    {
      // 현장 룰렛 키오스크 — 완전 공개(비로그인), 약관/공지/동의/앱전용 가드 전부 밖.
      // CommonRouter(Header/Footer/Language) 도 거치지 않는 독립 라우트. operator 가 링크/QR 생성.
      // [2026-10-22 B-F5] 현장 공개 URL 폐지 — 이미 뿌린 QR로 들어오면 앱 공지사항 참여 안내만.
      path: '/promotion/roulette/:promotionNo',
      element: (
        <Suspense fallback={<LazySpinner />}>
          <RouletteMoved />
        </Suspense>
      ),
    },
  ],
);

export default router;
